import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DotNavigationIcon,
} from '../Icons/Navigation';
import breakpoints from '../Theme/breakpoints';
import colors from '../Theme/colors';

const Carousel = styled.div<{ containerWidth?: string }>`
  position: relative;
  width: ${(properties) => properties.containerWidth};
  height: fit-content;
  display: flex;
  flex-wrap: wrap;
`;

const ImageContainer = styled.div<{ active?: boolean }>`
  display: ${(properties) => (properties.active ? 'flex' : 'none')};
  width: 100%;
`;

const NavigationContainer = styled.div`
  user-select: none;
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 34px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (any-hover: hover) {
    transition: opacity 0.5s;
    &:hover {
      opacity: 1;
    }
  }
`;

const Navigation = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.5s;

  &:hover{
    opacity: 1;

    svg path {
      fill: ${({ theme }) => theme.palette.primary.main};
      transition: fill 0.5s;
    }
  }

  svg {
    background: ${colors.white};
    border-radius: 50%;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 3px 3px -2px,
      rgba(0, 0, 0, 0.14) 0px 3px 4px 0px,
      rgba(0, 0, 0, 0.12) 0px 1px 8px 0px;
    margin: 0 8px;
    outline: none;

    @media (min-width: ${breakpoints.md}px) {
      margin: 0 16px;
    }
  }
`;

const DotNavigationContainer = styled.div`
  user-select: none;
  display: flex;
  flex: 0 0 calc(100% - 24px);
  justify-content: center;
  flex-wrap: wrap;
  padding: 12px;
`;

const DotNavigation = styled.div<{ active?: boolean }>`
  display: flex;
  opacity: 0.5;
  padding: 3px;
  transform: ${
    (properties) => (properties.active ? 'scale(1.75)' : 'scale(1)')
  };
  transition: transform 0.5s;

  svg {
    outline: none;
  }
`;

interface ImageCarouselProperties {
  children: React.ReactElement[];
  width?: string;
  active?: number;
  onNavigate?: (index: number) => void;
  slot?: string;
}

const ImageCarousel = ({
  children,
  width,
  active: controlledActive,
  onNavigate,
  slot,
}: ImageCarouselProperties) => {
  const { length } = children;
  const [internalActive, setInternalActive] = useState(0);
  const touchStartX = useRef<number | undefined>(undefined);
  const isControlled = controlledActive !== undefined
    && onNavigate !== undefined;
  const active = isControlled ? controlledActive : internalActive;
  const setActive = isControlled ? onNavigate : setInternalActive;
  const lastImage = length - 1;
  const firstImage = 0;
  const nextImage = active + 1;
  const previousImage = active - 1;

  const handleNavigateLeft = (event: React.MouseEvent) => {
    event.preventDefault();
    setActive(active <= firstImage ? lastImage : previousImage);
  };

  const handleNavigateRight = (event: React.MouseEvent) => {
    event.preventDefault();
    setActive(active >= lastImage ? firstImage : nextImage);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === undefined) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (deltaX > threshold) {
      setActive(active <= firstImage ? lastImage : previousImage);
    } else if (deltaX < -threshold) {
      setActive(active >= lastImage ? firstImage : nextImage);
    }
    touchStartX.current = undefined;
  };

  return (
    <Carousel
      containerWidth={width ?? '100%'}
      slot={slot}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <NavigationContainer>
        <Navigation
          data-testid="navigate"
          role="button"
          aria-label="Previous slide"
          onClickCapture={handleNavigateLeft}
        >
          <ArrowLeftIcon
            size="36"
          />
        </Navigation>
        <Navigation
          data-testid="navigate"
          role="button"
          aria-label="Next slide"
          onClickCapture={handleNavigateRight}
        >
          <ArrowRightIcon
            size="36"
          />
        </Navigation>
      </NavigationContainer>

      {children.map((image, imageIndex) => (
        <ImageContainer
          data-testid="image"
          key={image.key}
          active={imageIndex === active}
        >
          {image}
        </ImageContainer>
      ))}

      <DotNavigationContainer>
        {children.map(({ key }, imageIndex) => (
          <DotNavigation
            data-testid="navigate-dot"
            key={key}
            active={imageIndex === active}
          >
            <DotNavigationIcon
              size="4"
            />
          </DotNavigation>
        ))}
      </DotNavigationContainer>
    </Carousel>
  );
};

export default ImageCarousel;
